import type {
  BiomeId,
  ChatChannel,
  GuildRank,
  ModerateAction,
  WorldEventKind,
} from '@shefira/shared';

/**
 * What a line typed in the chat asks for (ADR 0033). A line starting with
 * « / » is a command; anything else is said on the chat's current channel.
 * A channel's command with a text says it there; alone, it switches the
 * chat to that channel. The commands only turn into intentions: the server
 * still decides who hears, who may invite, and who is found.
 */
export type ChatCommand =
  /** `channel` absent: the chat's current channel. */
  | { kind: 'say'; text: string; channel?: ChatChannel; to?: string }
  | { kind: 'switch'; channel: ChatChannel; to?: string }
  /** Answer the last private line received. */
  | { kind: 'reply'; text?: string }
  | { kind: 'invite'; name: string }
  | { kind: 'leave' }
  | { kind: 'kick'; name: string }
  | { kind: 'friend'; name: string }
  /** Propose an exchange to a player (ADR 0034). */
  | { kind: 'trade'; name: string }
  /** Hide a player's lines and refuse their invitations (ADR 0076), or stop. */
  | { kind: 'block'; name: string }
  | { kind: 'unblock'; name: string }
  /** Show « Absent » now (ADR 0080); the next action brings the player back. */
  | { kind: 'away' }
  | GuildCommand
  | ModerateCommand
  /** A game master's world event (ADR 0050): one of a biome now (an invasion unless named), or the end of the one under way. */
  | { kind: 'event'; action: 'start'; event: WorldEventKind; biome: BiomeId }
  | { kind: 'event'; action: 'stop' }
  /** Report a player (ADR 0037); the reason is asked in a window. */
  | { kind: 'report'; name: string }
  | { kind: 'help' }
  | { kind: 'unknown'; command: string };

/** The guild's verbs (ADR 0036): `/guilde inviter Bob`, `/guilde devise texte`, `/guilde` alone opens the window. */
export type GuildCommand =
  | { kind: 'guild'; action: 'invite' | 'kick' | 'promote' | 'demote'; name: string }
  | { kind: 'guild'; action: 'leave' | 'disband' | 'open' }
  | { kind: 'guild'; action: 'motd'; text: string };

/** An admin's sanctions (ADR 0037); a numeric second word is the duration, the rest the reason. */
export type ModerateCommand =
  | { kind: 'moderate'; action: 'mute'; name: string; minutes?: number; reason?: string }
  | { kind: 'moderate'; action: 'unmute' | 'unban'; name: string }
  | { kind: 'moderate'; action: 'kick'; name: string; reason?: string }
  | { kind: 'moderate'; action: 'ban'; name: string; days?: number; reason?: string };

type Verb =
  | ChatChannel
  | 'reply'
  | 'invite'
  | 'leave'
  | 'kick'
  | 'friend'
  | 'trade'
  | 'block'
  | 'unblock'
  | 'away'
  | 'guild'
  | 'report'
  /** `/kick` is the admin's (ADR 0037); the party's stays `/exclure`. */
  | 'mute'
  | 'unmute'
  | 'mod-kick'
  | 'ban'
  | 'unban'
  | 'event'
  | 'help';

/** French first, the habits of other games accepted too. */
const ALIASES: Partial<Record<string, Verb>> = {
  general: 'general',
  gen: 'general',
  tous: 'general',
  '1': 'general',
  z: 'zone',
  zone: 'zone',
  s: 'zone',
  g: 'party',
  groupe: 'party',
  p: 'party',
  party: 'party',
  gu: 'guild',
  gc: 'guild',
  m: 'private',
  mp: 'private',
  w: 'private',
  msg: 'private',
  r: 'reply',
  inviter: 'invite',
  invite: 'invite',
  quitter: 'leave',
  leave: 'leave',
  exclure: 'kick',
  ami: 'friend',
  friend: 'friend',
  echanger: 'trade',
  échanger: 'trade',
  trade: 'trade',
  bloquer: 'block',
  block: 'block',
  ignorer: 'block',
  ignore: 'block',
  debloquer: 'unblock',
  débloquer: 'unblock',
  unblock: 'unblock',
  unignore: 'unblock',
  absent: 'away',
  afk: 'away',
  away: 'away',
  guilde: 'guild',
  guild: 'guild',
  signaler: 'report',
  report: 'report',
  mute: 'mute',
  unmute: 'unmute',
  kick: 'mod-kick',
  ban: 'ban',
  unban: 'unban',
  event: 'event',
  evenement: 'event',
  événement: 'event',
  aide: 'help',
  help: 'help',
};

/**
 * `/gu` and `/gc` speak on the guild channel, `/guilde` and `/guild` manage
 * the guild: both map to the same verb, told apart by the head typed.
 */
const GUILD_CHANNEL_HEADS = new Set(['gu', 'gc']);

const GUILD_ACTIONS: Partial<Record<string, GuildCommand['action']>> = {
  inviter: 'invite',
  invite: 'invite',
  exclure: 'kick',
  kick: 'kick',
  promouvoir: 'promote',
  promote: 'promote',
  retrograder: 'demote',
  rétrograder: 'demote',
  demote: 'demote',
  quitter: 'leave',
  leave: 'leave',
  dissoudre: 'disband',
  disband: 'disband',
  devise: 'motd',
  motd: 'motd',
};

/** The rank a promotion or a demotion asks for. */
export const GUILD_RANK_OF_ACTION: Record<'promote' | 'demote', Exclude<GuildRank, 'leader'>> = {
  promote: 'officer',
  demote: 'member',
};

export const CHAT_HELP =
  '/general, /z, /g, /gu : parler à tous, à la zone, au groupe, à la guilde · /m nom message : message privé · /r : répondre · /inviter nom · /quitter · /exclure nom · /ami nom · /echanger nom · /signaler nom · /bloquer nom · /debloquer nom · /absent · /guilde inviter|exclure|promouvoir|retrograder nom, /guilde devise texte, /guilde quitter, /guilde dissoudre';

/** What an admin may type (ADR 0037, ADR 0050); shown to them only. */
export const MODERATION_HELP =
  '/mute nom [minutes] [raison] · /unmute nom · /kick nom [raison] · /ban nom [jours] [raison] · /unban nom · /event [invasion|geant|chariot] prairie|foret|marais|desert|neige · /event stop';

/** The scenario of `/event`, by its id or its French name; an invasion when none is named. */
const EVENT_KINDS: Partial<Record<string, WorldEventKind>> = {
  invasion: 'invasion',
  giant: 'giant',
  geant: 'giant',
  géant: 'giant',
  convoy: 'convoy',
  chariot: 'convoy',
};

/** The biome of `/event`, by its id or its French name, accents optional. */
const EVENT_BIOMES: Partial<Record<string, BiomeId>> = {
  meadow: 'meadow',
  prairie: 'meadow',
  forest: 'forest',
  foret: 'forest',
  forêt: 'forest',
  swamp: 'swamp',
  marais: 'swamp',
  desert: 'desert',
  désert: 'desert',
  snow: 'snow',
  neige: 'snow',
};

export function parseChatLine(line: string): ChatCommand {
  const text = line.trim();
  if (!text.startsWith('/')) return { kind: 'say', text };
  const [head = '', ...rest] = text.slice(1).split(/\s+/);
  const argument = rest.join(' ').trim();
  const verb = ALIASES[head.toLowerCase()];
  switch (verb) {
    case 'general':
    case 'zone':
    case 'party':
    case 'guild':
      if (verb === 'guild' && !GUILD_CHANNEL_HEADS.has(head.toLowerCase())) {
        return parseGuild(rest);
      }
      return argument.length > 0
        ? { kind: 'say', channel: verb, text: argument }
        : { kind: 'switch', channel: verb };
    case 'private': {
      const [to = '', ...words] = rest;
      const said = words.join(' ').trim();
      if (to.length === 0) return { kind: 'help' };
      return said.length > 0
        ? { kind: 'say', channel: 'private', to, text: said }
        : { kind: 'switch', channel: 'private', to };
    }
    case 'reply':
      return argument.length > 0 ? { kind: 'reply', text: argument } : { kind: 'reply' };
    case 'invite':
      return argument.length > 0 ? { kind: 'invite', name: argument } : { kind: 'help' };
    case 'kick':
      return argument.length > 0 ? { kind: 'kick', name: argument } : { kind: 'help' };
    case 'friend':
      return argument.length > 0 ? { kind: 'friend', name: argument } : { kind: 'help' };
    case 'trade':
      return argument.length > 0 ? { kind: 'trade', name: argument } : { kind: 'help' };
    case 'block':
    case 'unblock':
      return argument.length > 0 ? { kind: verb, name: argument } : { kind: 'help' };
    case 'report':
      return argument.length > 0 ? { kind: 'report', name: argument } : { kind: 'help' };
    case 'leave':
      return { kind: 'leave' };
    case 'away':
      return { kind: 'away' };
    case 'mute':
    case 'unmute':
    case 'ban':
    case 'unban':
      return parseModeration(verb, rest);
    case 'mod-kick':
      return parseModeration('kick', rest);
    case 'event': {
      const word = argument.toLowerCase();
      if (word === 'stop' || word === 'arreter' || word === 'arrêter') {
        return { kind: 'event', action: 'stop' };
      }
      const [first = '', second = ''] = word.split(/\s+/);
      const named = EVENT_KINDS[first];
      const biome = EVENT_BIOMES[named === undefined ? first : second];
      return biome === undefined
        ? { kind: 'help' }
        : { kind: 'event', action: 'start', event: named ?? 'invasion', biome };
    }
    case 'help':
      return { kind: 'help' };
    case undefined:
      return { kind: 'unknown', command: head };
  }
}

function parseGuild(words: readonly string[]): ChatCommand {
  const [sub = '', ...rest] = words;
  if (sub.length === 0) return { kind: 'guild', action: 'open' };
  const action = GUILD_ACTIONS[sub.toLowerCase()];
  const argument = rest.join(' ').trim();
  switch (action) {
    case 'invite':
    case 'kick':
    case 'promote':
    case 'demote':
      return argument.length > 0 ? { kind: 'guild', action, name: argument } : { kind: 'help' };
    case 'motd':
      // An empty motto is allowed: it clears the current one.
      return { kind: 'guild', action, text: argument };
    case 'leave':
    case 'disband':
    case 'open':
      return { kind: 'guild', action };
    case undefined:
      return { kind: 'help' };
  }
}

/**
 * `/mute Bob 30 flood` : name, then a number when the sanction has a
 * duration, then the reason in the words left. `/kick` has no duration, so
 * its second word already belongs to the reason.
 */
function parseModeration(action: ModerateAction, words: readonly string[]): ChatCommand {
  const [name = '', ...rest] = words;
  if (name.length === 0) return { kind: 'help' };
  switch (action) {
    case 'unmute':
    case 'unban':
      return { kind: 'moderate', action, name };
    case 'kick': {
      const reason = rest.join(' ').trim();
      return { kind: 'moderate', action, name, ...(reason.length > 0 && { reason }) };
    }
    case 'mute':
    case 'ban': {
      const [first = '', ...others] = rest;
      const duration = /^\d+$/.test(first) ? Number(first) : undefined;
      const reason = (duration === undefined ? rest : others).join(' ').trim();
      const spelt = reason.length > 0 ? { reason } : {};
      if (action === 'mute') {
        const minutes = duration !== undefined ? { minutes: duration } : {};
        return { kind: 'moderate', action, name, ...minutes, ...spelt };
      }
      const days = duration !== undefined ? { days: duration } : {};
      return { kind: 'moderate', action, name, ...days, ...spelt };
    }
  }
}
